import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Text, HStack, Spinner } from '@chakra-ui/react';
import MessageBubble from './MessageBubble';
import WhatsUpBubble from './WhatsUpBubble';
import EndConversationNotice from './EndConversationNotice';
import TypingIndicator from './TypingIndicator';
import DateSeparator from './DateSeparator';
import StreamingBubble from './StreamingBubble';
import { WHATSAPP_ENABLED } from '../featureFlags';

const MessageList = ({ messages, pendingUserMessage, isBotTyping, streamingState, colors, onScroll, onWhatsUpYes, onWhatsUpNo, reconnectState, onItemPress, scrollToBottomSignal }) => {
    const { t } = useTranslation('chat');
    const bottomRef = useRef(null);
    const scrollRef = useRef(null);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isBotTyping, streamingState?.displayText]);

    useEffect(() => {
        if (!scrollToBottomSignal) return undefined;
        let raf;
        const start = performance.now();
        const tick = (now) => {
            const el = scrollRef.current;
            if (el) el.scrollTop = el.scrollHeight;
            if (now - start < 700) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [scrollToBottomSignal]);

    return (
        <Box
            ref={scrollRef}
            flex={1}
            minH={0}
            overflowY="auto"
            overscrollBehavior="contain"
            bg={colors.chatBg}
            display="flex"
            flexDirection="column"
            onScroll={onScroll}
            style={{ WebkitOverflowScrolling: 'touch' }}
            css={{
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { background: colors.borderColor, borderRadius: '4px' },
            }}
        >
            <DateSeparator colors={colors} />

            {messages.map(message => {
                const isTextLike = message.type !== 'image' && message.type !== 'file' && message.type !== 'document'
                    && typeof message.text === 'string';

                if (isTextLike && message.text.includes('{%WhatsApp_Start}')) {
                    if (!WHATSAPP_ENABLED) {
                        const plainMessage = { ...message, text: message.text.replace('{%WhatsApp_Start}', '') };
                        return <MessageBubble key={message.id} message={plainMessage} colors={colors} onItemPress={onItemPress} />;
                    }
                    return (
                        <WhatsUpBubble
                            key={message.id}
                            message={message}
                            colors={colors}
                            onYes={onWhatsUpYes}
                            onNo={onWhatsUpNo}
                        />
                    );
                }
                if (message.endConversation === true || (isTextLike && message.text.includes('{%EndConversation}'))) {
                    return (
                        <EndConversationNotice key={message.id} message={message} colors={colors} />
                    );
                }
                return <MessageBubble key={message.id} message={message} colors={colors} onItemPress={onItemPress} />;
            })}

            {pendingUserMessage && !messages.some(m => m.id === pendingUserMessage.id) && (
                <MessageBubble key={pendingUserMessage.id} message={pendingUserMessage} colors={colors} />
            )}

            {streamingState && (
                <StreamingBubble text={streamingState.displayText || '...'} colors={colors} />
            )}

            {isBotTyping && !streamingState && (
                <TypingIndicator colors={colors} />
            )}

            {reconnectState && (
                <Box display="flex" justifyContent="center" px={6} py={3}>
                    <HStack gap="8px" alignItems="center">
                        <Spinner size="xs" color={colors.textSecondary} />
                        <Text fontSize="sm" color={colors.textSecondary}>
                            {reconnectState.offline
                                ? t('reconnect.offline')
                                : t('reconnect.retry', { attempt: reconnectState.attempt })}
                        </Text>
                    </HStack>
                </Box>
            )}

            <Box ref={bottomRef} h="8px" />
        </Box>
    );
};

export default MessageList;
